import { FC, ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Avatar, Button, Card, Col, Grid, Row, Typography } from 'antd';
import { TbChevronDown, TbDatabase, TbFileText, TbLink, TbWorld } from 'react-icons/tb';
import { useNavigate } from 'react-router-dom';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import { useMaps } from '@/hooks/useMaps';
import { useWorkspaceResourceWebsite } from '@/hooks/useWorkspaceResourceWebsite';
import EmptySection from '@/shared/components/layouts/views/EmptySection';
import { AvatarSkeleton, ButtonSkeleton } from '@/shared/components/skeletons';
import { WorkspaceResourceAvatar } from '@/shared/components/workspace-resources';
import { ellipsisStyling, fetchMetadata } from '@/utilities';

import { OurDocsLink } from '../../tags';
import { getFilingSourceTruthUrl } from '../filingSourceNavigation';
import UnfurledLinkPreview from '../UnfurledLinkPreview';
import {
    mergeSources,
    parseHtmlBody,
    scanSourceRoot,
    sourceFingerprint,
    sourcesFromBody,
    titleFromUrl,
} from './sourceScanning';
import { MemoSourceItem, MemoSourcesProps } from './types';

export type { MemoSourceItem } from './types';

const EXTERNAL_METADATA_LIMIT = 12;
const VIEWPORT_GUTTER = 12;
const SOURCE_PANEL_TOP = '5rem';

const SourceSection: FC<{
    children: ReactNode;
    count: number;
    defaultCollapsed?: boolean;
    emptyDescription: string;
    emptyIcon: ReactNode;
    emptyLabel: string;
    icon: ReactNode;
    title: string;
}> = ({ children, count, defaultCollapsed = true, emptyDescription, emptyIcon, emptyLabel, icon, title }) => {
    const { token } = useAntDesignTheme();
    const [collapsed, setCollapsed] = useState(defaultCollapsed);

    return (
        <section style={{ marginTop: '0.5rem' }}>
            <Button
                block
                type='text'
                size='small'
                style={{
                    textAlign: 'left',
                    marginBottom: collapsed ? 0 : '0.5rem',
                    justifyContent: 'flex-start',
                }}
                onClick={() => setCollapsed((value) => !value)}
            >
                <Row style={{ width: '100%' }} align='middle' wrap={false}>
                    <Col flex='auto' style={{ minWidth: 0 }}>
                        <small style={{ color: token.colorTextTertiary }}>
                            <span style={{ marginRight: '0.25rem', verticalAlign: '-0.125rem' }}>{icon}</span>
                            {title} ({count})
                        </small>
                    </Col>
                    <Col flex='none'>
                        <TbChevronDown
                            size='0.875rem'
                            style={{
                                color: token.colorTextTertiary,
                                transform: collapsed ? 'rotate(-90deg)' : undefined,
                                transition: 'transform 250ms ease',
                                verticalAlign: '-0.125rem',
                            }}
                        />
                    </Col>
                </Row>
            </Button>
            {!collapsed &&
                (count > 0 ? (
                    children
                ) : (
                    <EmptySection height={16 * 10} icon={emptyIcon} label={emptyLabel} description={emptyDescription} />
                ))}
        </section>
    );
};

const SourceSectionSkeleton: FC<{
    icon: ReactNode;
    rows?: number;
    title: string;
}> = ({ icon, rows = 2, title }) => {
    const { token } = useAntDesignTheme();

    return (
        <section style={{ marginTop: '0.5rem' }}>
            <div style={{ padding: '0 0.25rem', marginBottom: '0.5rem' }}>
                <small style={{ color: token.colorTextTertiary }}>
                    <span style={{ marginRight: '0.25rem', verticalAlign: '-0.125rem' }}>{icon}</span>
                    {title}
                </small>
            </div>
            {Array.from({ length: rows }).map((_, index) => (
                <Card
                    key={`${title}-${index}`}
                    styles={{ body: { padding: '0.5rem 0.625rem' } }}
                    style={{ marginBottom: '0.5rem' }}
                >
                    <Row align='top' wrap={false} gutter={[8, 0]}>
                        <Col flex='none'>
                            <AvatarSkeleton size={16} />
                        </Col>
                        <Col flex='auto' style={{ minWidth: 0 }}>
                            <ButtonSkeleton
                                block
                                style={{
                                    display: 'block',
                                    height: '0.875rem',
                                    marginBottom: '0.375rem',
                                    width: index % 2 === 0 ? '72%' : '56%',
                                }}
                            />
                            <ButtonSkeleton
                                block
                                style={{
                                    display: 'block',
                                    height: '0.75rem',
                                    width: index % 2 === 0 ? '48%' : '64%',
                                }}
                            />
                        </Col>
                    </Row>
                </Card>
            ))}
        </section>
    );
};

const MemoSourceCard: FC<{
    icon: ReactNode;
    onClick?: () => void;
    source: MemoSourceItem;
}> = ({ icon, onClick, source }) => {
    const { token } = useAntDesignTheme();
    const [hovered, setHovered] = useState(false);
    const workspaceResourceType =
        source.kind === 'internal' &&
        source.type !== undefined &&
        source.type !== 'filing' &&
        source.type !== 'report-component'
            ? source.type
            : undefined;
    const resourceWebsite = useWorkspaceResourceWebsite(workspaceResourceType, source.resourceId);

    return (
        <Card
            onClick={onClick}
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}
            style={{
                marginBottom: '0.5rem',
                textAlign: 'left',
                cursor: onClick ? 'pointer' : 'default',
                backgroundColor: hovered ? token.colorBgBase : undefined,
            }}
            styles={{ body: { padding: '0.5rem 0.625rem' } }}
        >
            <div style={{ minWidth: 0 }}>
                <Row align='middle' wrap={false} gutter={[8, 0]}>
                    <Col flex='none'>
                        {workspaceResourceType ? (
                            <WorkspaceResourceAvatar
                                resource={workspaceResourceType}
                                size={16}
                                src={source.logo ?? source.image}
                                website={resourceWebsite}
                            />
                        ) : (
                            <Avatar
                                size={16}
                                shape='square'
                                src={source.logo ?? source.image}
                                icon={icon}
                                style={{
                                    border: `1px solid ${token.colorBorder}`,
                                    background: token.colorBgBase,
                                    color: token.colorTextTertiary,
                                    display: 'inline-flex',
                                }}
                            />
                        )}
                    </Col>
                    <Col flex='auto' style={{ minWidth: 0 }}>
                        <Typography.Text strong style={{ display: 'block', lineHeight: '1.125rem' }} ellipsis>
                            {source.title}
                        </Typography.Text>
                    </Col>
                </Row>
                <div style={{ paddingLeft: '1.5rem' }}>
                    <small
                        style={{
                            color: token.colorTextTertiary,
                            display: 'block',
                            marginTop: '0.125rem',
                            ...ellipsisStyling,
                        }}
                    >
                        {source.description || source.label}
                    </small>
                </div>
            </div>
        </Card>
    );
};

const MemoSources: FC<MemoSourcesProps> = ({
    body,
    contentRef,
    filingNavigation = 'internal',
    loading = false,
    maxContentWidth = 960,
    metadataMode = 'none',
    padding = '0 1rem',
    placement = 'inline',
    onSelect,
    sticky = true,
}) => {
    const { token } = useAntDesignTheme();
    const { reportComponentTypeToIconMap } = useMaps();
    const navigate = useNavigate();
    const screens = Grid.useBreakpoint();
    const bodyRef = useRef(body);
    const pendingMetadataUrlsRef = useRef(new Set<string>());
    const scanTimeoutRef = useRef<number | null>(null);
    const sourcesFingerprintRef = useRef('');
    const [metadataCache, setMetadataCache] = useState<Record<string, Partial<MemoSourceItem>>>({});
    const [sources, setSources] = useState<MemoSourceItem[]>(() => sourcesFromBody(body));
    const [metadataLoadingUrls, setMetadataLoadingUrls] = useState<string[]>([]);
    const [fixedVisible, setFixedVisible] = useState(false);
    const [fixedLeft, setFixedLeft] = useState<number>();
    const [fixedWidth, setFixedWidth] = useState<number>();

    const refreshSources = useCallback(
        (options?: { includeBody?: boolean }) => {
            const domSources = scanSourceRoot(contentRef?.current);
            const bodySources =
                options?.includeBody || !domSources.length ? scanSourceRoot(parseHtmlBody(bodyRef.current)) : [];
            const nextSources = mergeSources(domSources, bodySources);
            const nextFingerprint = sourceFingerprint(nextSources);

            if (sourcesFingerprintRef.current === nextFingerprint) {
                return;
            }

            sourcesFingerprintRef.current = nextFingerprint;
            setSources(nextSources);
        },
        [contentRef],
    );

    const scheduleRefreshSources = useCallback(() => {
        if (scanTimeoutRef.current) {
            window.clearTimeout(scanTimeoutRef.current);
        }

        scanTimeoutRef.current = window.setTimeout(() => refreshSources(), 250);
    }, [refreshSources]);

    const updateFixedPosition = useCallback(() => {
        if (placement !== 'fixed') {
            return;
        }

        const rect = contentRef?.current?.getBoundingClientRect();
        const contentRight = rect?.right ?? (window.innerWidth + maxContentWidth) / 2;
        const left = contentRight + 24;
        const availableWidth = window.innerWidth - left - VIEWPORT_GUTTER;
        const nextVisible = Boolean(screens.xl && availableWidth >= 240);

        setFixedVisible(nextVisible);
        setFixedLeft(nextVisible ? left : undefined);
        setFixedWidth(nextVisible ? availableWidth : undefined);
    }, [contentRef, maxContentWidth, placement, screens.xl]);

    useEffect(() => {
        const initialRefreshFrame = window.requestAnimationFrame(() => {
            refreshSources({ includeBody: true });
        });

        const root = contentRef?.current;

        if (!root) {
            return () => window.cancelAnimationFrame(initialRefreshFrame);
        }

        const observer = new MutationObserver(scheduleRefreshSources);

        observer.observe(root, {
            attributeFilter: [
                'href',
                'data-url',
                'data-title',
                'data-description',
                'data-company',
                'data-filing',
                'data-filing-source',
                'data-logo',
                'data-logo-url',
                'data-image',
                'data-memo-report-component-id',
                'data-report-component-id',
                'data-memo-report-component-type',
                'data-memo-toc-title',
                'data-source-url',
                'data-website',
            ],
            attributes: true,
            characterData: true,
            childList: true,
            subtree: true,
        });

        return () => {
            window.cancelAnimationFrame(initialRefreshFrame);
            observer.disconnect();

            if (scanTimeoutRef.current) {
                window.clearTimeout(scanTimeoutRef.current);
            }
        };
    }, [contentRef, refreshSources, scheduleRefreshSources]);

    useEffect(() => {
        bodyRef.current = body;

        if (!contentRef?.current) {
            const refreshFrame = window.requestAnimationFrame(() => {
                refreshSources({ includeBody: true });
            });

            return () => window.cancelAnimationFrame(refreshFrame);
        }

        return undefined;
    }, [body, contentRef, refreshSources]);

    useEffect(() => {
        const positionFrame = window.requestAnimationFrame(updateFixedPosition);

        if (placement !== 'fixed') {
            return () => window.cancelAnimationFrame(positionFrame);
        }

        window.addEventListener('resize', updateFixedPosition);
        window.addEventListener('scroll', updateFixedPosition, true);

        return () => {
            window.cancelAnimationFrame(positionFrame);
            window.removeEventListener('resize', updateFixedPosition);
            window.removeEventListener('scroll', updateFixedPosition, true);
        };
    }, [placement, updateFixedPosition]);

    const externalSources = useMemo(
        () =>
            sources
                .filter((source) => source.kind === 'external' && source.url)
                .map((source) => ({
                    ...source,
                    ...(source.url ? metadataCache[source.url] : {}),
                })),
        [metadataCache, sources],
    );
    const internalSources = useMemo(() => sources.filter((source) => source.kind === 'internal'), [sources]);
    const externalUrlsKey = externalSources.map((source) => source.url).join('|');

    useEffect(() => {
        if (metadataMode === 'none') {
            return;
        }

        const urlsToFetch = externalUrlsKey
            .split('|')
            .filter((url): url is string => Boolean(url))
            .filter((url) => !metadataCache[url] && !pendingMetadataUrlsRef.current.has(url))
            .slice(0, EXTERNAL_METADATA_LIMIT);

        urlsToFetch.forEach((url) => {
            pendingMetadataUrlsRef.current.add(url);
            setMetadataLoadingUrls((urls) => (urls.includes(url) ? urls : [...urls, url]));

            fetchMetadata(url, { mode: metadataMode })
                .then((metadata) => {
                    const openGraph = metadata?.openGraph;

                    if (!openGraph) {
                        setMetadataCache((cache) => ({ ...cache, [url]: {} }));
                        return;
                    }

                    setMetadataCache((cache) => ({
                        ...cache,
                        [url]: {
                            author: openGraph.author,
                            date: openGraph.date,
                            description: openGraph.description,
                            image: openGraph.image?.url,
                            logo: openGraph.logo?.url,
                            publisher: openGraph.publisher,
                            title: openGraph.title || titleFromUrl(url),
                            url,
                        },
                    }));
                })
                .catch(() => {
                    setMetadataCache((cache) => ({ ...cache, [url]: {} }));
                })
                .finally(() => {
                    pendingMetadataUrlsRef.current.delete(url);
                    setMetadataLoadingUrls((urls) => urls.filter((loadingUrl) => loadingUrl !== url));
                });
        });
    }, [externalUrlsKey, metadataCache, metadataMode]);

    const scrollToSource = (source: MemoSourceItem) => {
        if (filingNavigation === 'source' && source.type === 'filing') {
            const sourceTruthUrl = getFilingSourceTruthUrl(source.url);

            if (sourceTruthUrl) {
                window.open(sourceTruthUrl, '_blank', 'noopener,noreferrer');
            }

            return;
        }

        const root = contentRef?.current;
        const element = source.reportComponentId
            ? root?.querySelector<HTMLElement>(
                  `[data-memo-report-component-id="${source.reportComponentId}"], [data-report-component-id="${source.reportComponentId}"]`,
              )
            : undefined;

        if (element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'center' });
            return;
        }

        if (source.url) {
            const url = new URL(source.url, window.location.origin);

            if (url.origin === window.location.origin) {
                navigate(`${url.pathname}${url.search}${url.hash}`);
                return;
            }

            window.open(source.url, '_blank', 'noopener,noreferrer');
        }
    };

    const iconForInternalSource = (source: MemoSourceItem) => {
        if (source.type === 'report-component') {
            return source.reportComponentType ? (
                reportComponentTypeToIconMap[source.reportComponentType]
            ) : (
                <TbDatabase size='75%' />
            );
        }

        if (source.type === 'filing') {
            return <TbFileText size='75%' />;
        }

        return <TbLink size='75%' />;
    };

    const content = loading ? (
        <div>
            <div style={{ marginBottom: '0.75rem' }}>
                <ButtonSkeleton
                    block
                    style={{ display: 'block', height: '1rem', marginBottom: '0.5rem', width: '36%' }}
                />
                <ButtonSkeleton block style={{ display: 'block', height: '0.75rem', width: '92%' }} />
            </div>
            <SourceSectionSkeleton title='Internal' icon={<TbDatabase size='0.875rem' />} />
            <SourceSectionSkeleton title='External' icon={<TbWorld size='0.875rem' />} />
        </div>
    ) : (
        <div>
            <div style={{ marginBottom: '0.75rem' }}>
                <Typography.Text strong>Sources</Typography.Text>
                <Typography.Paragraph style={{ margin: '0.125rem 0 0', color: token.colorTextTertiary }}>
                    Trace linked resources and external references in this memo. Learn more in <OurDocsLink />
                </Typography.Paragraph>
            </div>
            <SourceSection
                title='Internal'
                count={internalSources.length}
                icon={<TbDatabase size='0.875rem' />}
                emptyIcon={<TbDatabase size='75%' style={{ color: token.colorTextTertiary }} />}
                emptyLabel='No Internal Sources'
                emptyDescription='Linked workspace resources and embedded report components will appear here.'
            >
                {internalSources.map((source) => (
                    <MemoSourceCard
                        key={source.key}
                        source={source}
                        icon={iconForInternalSource(source)}
                        onClick={() => {
                            onSelect?.();
                            scrollToSource(source);
                        }}
                    />
                ))}
            </SourceSection>
            <SourceSection
                title='External'
                count={externalSources.length}
                icon={<TbWorld size='0.875rem' />}
                emptyIcon={<TbWorld size='75%' style={{ color: token.colorTextTertiary }} />}
                emptyLabel='No External Sources'
                emptyDescription='External URLs and enriched links will appear here.'
            >
                {externalSources.map((source) => (
                    <UnfurledLinkPreview
                        key={source.key}
                        author={source.author}
                        date={source.date}
                        density='compact'
                        description={source.description}
                        image={source.image}
                        isLoading={Boolean(
                            metadataMode !== 'none' &&
                            source.url &&
                            !Object.prototype.hasOwnProperty.call(metadataCache, source.url) &&
                            !source.description &&
                            !source.image &&
                            !source.logo &&
                            !source.publisher &&
                            (metadataLoadingUrls.includes(source.url) ||
                                externalSources.length <= EXTERNAL_METADATA_LIMIT),
                        )}
                        logo={source.logo}
                        publisher={source.publisher}
                        title={source.title}
                        url={source.url}
                        onClick={
                            source.url
                                ? () => {
                                      onSelect?.();
                                      window.open(source.url, '_blank', 'noopener,noreferrer');
                                  }
                                : undefined
                        }
                        style={{ marginBottom: '0.5rem' }}
                    />
                ))}
            </SourceSection>
        </div>
    );

    if (placement === 'fixed') {
        if (!fixedVisible || !fixedLeft || !fixedWidth) {
            return null;
        }

        return (
            <aside
                data-memo-sources-panel='true'
                style={{
                    position: 'fixed',
                    top: SOURCE_PANEL_TOP,
                    left: fixedLeft,
                    width: fixedWidth,
                    maxHeight: `calc(100vh - ${SOURCE_PANEL_TOP} - 5rem)`,
                    overflow: 'auto',
                    paddingRight: '0.25rem',
                }}
            >
                {content}
            </aside>
        );
    }

    return (
        <aside
            data-memo-sources-panel='true'
            style={{
                maxHeight: sticky ? `calc(100vh - ${SOURCE_PANEL_TOP} - 2rem)` : undefined,
                overflow: sticky ? 'auto' : undefined,
                padding,
                position: sticky ? 'sticky' : undefined,
                top: sticky ? SOURCE_PANEL_TOP : undefined,
            }}
        >
            {content}
        </aside>
    );
};

export default MemoSources;
